import React, { useState, useMemo } from 'react';
import { DatePicker, Button, Space, Input, Tooltip, Select, Popover, Badge, Form, Divider } from 'antd';
import { SearchOutlined, ReloadOutlined, FilterOutlined, TableOutlined, CheckCircleOutlined } from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/tr_TR';
import 'dayjs/locale/tr';

/**
 * Kontrol paneli bileşeni - Ay seçimi, arama ve yeniden hesaplama butonları içerir
 */
const ControlPanel = ({
  selectedMonth,
  searchValue,
  loading,
  searchInputRef,
  handleMonthChange,
  handleRecalculateAllBrands,
  setSearchValue,
  setIsPickerOpen,
  isPickerOpen,
  isEmbedded = false,
  isAllBrandsToken = false,
  advisors = [],
  advisorsLoading = false,
  selectedAdvisor,
  handleAdvisorChange,
  
  // Yeni advisor filtreleri
  brandGrowthAdvisors = [],
  brandAssistantAdvisors = [],
  brandMetaAdvisors = [],
  brandGoogleAdvisors = [],
  brandSeoAdvisors = [],
  selectedGrowth,
  selectedAssistant,
  selectedMeta,
  selectedGoogle,
  selectedSeo,
  handleGrowthChange,
  handleAssistantChange,
  handleMetaChange,
  handleGoogleChange,
  handleSeoChange,
  
  selectedStatus,
  handleStatusChange,
  onMetricSelectorClick,
  hideMetricSelector = false
}) => {
  const [filtersPopoverVisible, setFiltersPopoverVisible] = useState(false);
  const [form] = Form.useForm();
  
  // Aktif filtre sayısını hesapla
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedAdvisor) count++;
    if (selectedGrowth) count++;
    if (selectedAssistant) count++;
    if (selectedMeta) count++;
    if (selectedGoogle) count++;
    if (selectedSeo) count++;
    if (selectedStatus) count++;
    return count;
  }, [selectedAdvisor, selectedGrowth, selectedAssistant, selectedMeta, selectedGoogle, selectedSeo, selectedStatus]);
  
  // Form initial values
  const formInitialValues = {
    advisor: selectedAdvisor,
    growth: selectedGrowth,
    assistant: selectedAssistant,
    meta: selectedMeta,
    google: selectedGoogle,
    seo: selectedSeo,
    status: selectedStatus
  };
  
  // Form değerlerini uygula
  const handleApplyFilters = () => {
    const values = form.getFieldsValue();
    handleAdvisorChange(values.advisor || null);
    handleGrowthChange(values.growth || null);
    handleAssistantChange(values.assistant || null);
    handleMetaChange(values.meta || null);
    handleGoogleChange(values.google || null);
    handleSeoChange(values.seo || null);
    handleStatusChange(values.status || null);
    setFiltersPopoverVisible(false);
  };
  
  // Tüm filtreleri temizle
  const handleClearFilters = () => {
    form.resetFields();
    handleAdvisorChange(null);
    handleGrowthChange(null);
    handleAssistantChange(null);
    handleMetaChange(null);
    handleGoogleChange(null);
    handleSeoChange(null);
    handleStatusChange(null);
    setFiltersPopoverVisible(false);
  };
  
  // Popover içeriği
  const filtersContent = (
    <div style={{ width: 400, padding: '8px 0' }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={formInitialValues}
        size="small"
      >
        <Form.Item label="Advisor" name="advisor">
          <Select
            placeholder="Advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {advisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="Growth" name="growth">
          <Select
            placeholder="Growth advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {brandGrowthAdvisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="Assistant" name="assistant">
          <Select
            placeholder="Assistant advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {brandAssistantAdvisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="Meta/Facebook" name="meta">
          <Select
            placeholder="Meta advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {brandMetaAdvisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="Google Ads" name="google">
          <Select
            placeholder="Google Ads advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {brandGoogleAdvisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="SEO" name="seo">
          <Select
            placeholder="SEO advisor seçiniz"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            loading={advisorsLoading}
          >
            {brandSeoAdvisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.advisor_name}>
                {advisor.advisor_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        
        <Form.Item label="Durum" name="status">
          <Select
            placeholder="Durum seçiniz"
            allowClear
          >
            <Select.Option value="success">Başarılı</Select.Option>
            <Select.Option value="unsuccess">Başarısız</Select.Option>
          </Select>
        </Form.Item>
        
        <Divider style={{ margin: '12px 0' }} />
        
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={handleClearFilters} disabled={activeFilterCount === 0}>
            Temizle
          </Button>
          <Button type="primary" onClick={handleApplyFilters}>
            Uygula
          </Button>
        </Space>
      </Form>
    </div>
  );
  
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '10px'
    }}>
      <Space wrap>
        <DatePicker
          picker="month"
          format="MMMM YYYY"
          allowClear={false}
          value={selectedMonth}
          onChange={handleMonthChange}
          locale={locale}
          open={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          style={{ width: '150px' }}
          disabled={loading}
        />
        
        {/* Arama kutusu tüm markalar için görünür */}
        <Input
          ref={searchInputRef}
          placeholder="Marka ara..."
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        
        {/* Gelişmiş Filtreler */}
        <Popover
          content={filtersContent}
          title="Gelişmiş Filtreler"
          trigger="click"
          placement="bottomLeft"
          visible={filtersPopoverVisible}
          onVisibleChange={setFiltersPopoverVisible}
        >
          <Badge count={activeFilterCount} size="small" offset={[-5, 5]}>
            <Button
              icon={<FilterOutlined />}
              type={activeFilterCount > 0 ? "primary" : "default"}
            >
              Filtreler
            </Button>
          </Badge>
        </Popover>
        
        
        {/* Metrik Seçici Butonu */}
        {!hideMetricSelector && (
          <Tooltip title="Metrik Özeti Oluştur">
            <Button 
              type="primary"
              icon={<TableOutlined />}
              onClick={onMetricSelectorClick}
            >
              Metrik Özeti
            </Button>
          </Tooltip>
        )}
        
      </Space>
      
      {/* Embed görünümünde yeniden hesaplama butonu yok */}
      {!isEmbedded && (
        <Tooltip title="Tüm markaların metriklerini yeniden hesapla">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRecalculateAllBrands}
            loading={loading}
          >
            Yeniden Hesapla
          </Button>
        </Tooltip>
      )}
      
      {/* Token ile görüntülendiğinde bilgi metni */}
      {isEmbedded && isAllBrandsToken && (
        <div>
          <span style={{ fontStyle: 'italic', color: '#52c41a' }}>
            Tüm markalar embed görünümü
          </span>
        </div>
      )}
    </div>
  );
};

export default ControlPanel; 